import axios from "axios";
import { UserDetails } from "../../types";
import api from "../api";
import {
    ChangeEmailConfirmResponse,
    ChangeEmailResponse,
    ChangePasswordResponse,
    ForgotPasswordResponse,
    LoginResponse,
    objectToFormData,
    PasswordSetupResponse,
    RegisterRequest,
    RegisterResponse,
    ResetPasswordResponse,
} from "./types";
class AccountApi {
    private baseUrl = "/account";

    private pwStrict: boolean | null = true;

    me = async (): Promise<UserDetails> => {
        const { data } = await api.get(this.baseUrl + "/me");
        return data as UserDetails;
    }

    logIn = async (email, password): Promise<LoginResponse> => {
        try {
            const { data } = await api.post(this.baseUrl + "/login", {
                email,
                password,
            });
            return data as LoginResponse;
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    logOut = async (keepSession: boolean): Promise<void> => {
        if (keepSession) 
            await api.post(this.baseUrl + "/logout-keep-session");
        else
            await api.post(this.baseUrl + "/logout");
    }

    register = async (data: RegisterRequest): Promise<RegisterResponse> => {
        try {
            const url = `${this.baseUrl}/register`;

            const formData = objectToFormData(data);
            const response = await api.post(url, formData);

            const json = response.data;
            return {
                success: json.success === true,
                error: json.success ? null : json.error || "Registration failed",
                locked: false,
            };
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                const status = error.response.status;
                return {
                    success: false,
                    error: (error.response.data as string) || "Registration failed",
                    locked: status === 429,
                };
            }

            return {
                success: false,
                error: error instanceof Error ? error.message : "Unexpected error",
                locked: false,
            };
        }
    };

    changePassword = async (currentPassword: string, newPassword: string): Promise<ChangePasswordResponse> => {
        try {
            const url = `${this.baseUrl}/change-password`;
            const { data } = await api.post(url, { currentPassword, newPassword });
            return { success: data.success };
        } catch (error) {
            if (axios.isAxiosError(error) && error.response) {
                console.error(error);
                return {
                    success: false,
                    error: (error.response.data as string) || "Change password failed",
                };
            }

            return null;
        }
    };

    deleteAccount = async (feedback: string): Promise<boolean> => {
        try {
            let url = `${this.baseUrl}/delete-account`;
            const response = await api.post(url, { feedback });
            return response.status == 200;
        } catch (error) {
            console.error(error);
            return false;
        }
    };

    forgotPassword = async (email: string): Promise<ForgotPasswordResponse> => {
        const url = `${this.baseUrl}/forgot-password`;
        const { data } = await api.post(url, { email });
        return { success: true, locked: data.locked };
    };

    resetPassword = async (resetToken: string, email: string, newPassword: string): Promise<ResetPasswordResponse> => {
        const { data } = await api.post(`${this.baseUrl}/reset-password`, {
            resetToken,
            email,
            newPassword,
        });
        return { success: data.success, tokenExpired: data.tokenExpired };
    };

    setupPassword = async (newPassword: string): Promise<PasswordSetupResponse> => {
        const { data } = await api.post <PasswordSetupResponse>(`${this.baseUrl}/setup-password`, null, { params: { password: newPassword } });
        return data;
    }

    requestEmailChange = async (currentEmail: string, newEmail: string, password: string): Promise<ChangeEmailResponse> => {
        const { data } = await api.post(`${this.baseUrl}/change-email/request`, {
            currentEmail: currentEmail,
            newEmail: newEmail,
            password: password,
        });
        return data;
    }

    confirmEmailChange = async (userId: string, newEmail: string, token: string): Promise<ChangeEmailConfirmResponse> => {
        const { data } = await api.post(`${this.baseUrl}/change-email/confirm`, {
            userId: userId,
            newEmail: newEmail,
            token: token,
        });
        return data;
    }

    isAuthenticated = async () => {
        const { data } = await api.get(this.baseUrl + "/me");
        return data;
    };

    isPwStrict = async (): Promise<boolean> => {
        if (this.pwStrict != null) return this.pwStrict;

        try {
            const url = `${this.baseUrl}/pw-strict`;
            const { data } = await api.get(url);
            this.isPwStrict = data.pwStrict;
            return data;
        } catch (error) {
            console.error(error);
            return true;
        }
    };

    demoLogin = async (password) => {
        try {
            const url = `${this.baseUrl}/demo-login`;
            const { data } = await api.get(url, { params: { password } });
            return data.secret;
        } catch (error) {
            console.error(error);
            return null;
        }
    };
}

export default new AccountApi();
